import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BarChart3, TrendingUp, Users, Mail, Eye, MousePointer, CheckCircle, XCircle, Clock, Target, ArrowUpRight, ArrowDownRight, Calendar, Filter } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { AreaChart, Area, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, CartesianGrid, XAxis, YAxis, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from "recharts";
import { format, subDays } from "date-fns";

interface AnalyticsOverview {
  totalCampaigns: number;
  activeCampaigns: number;
  totalJourneys: number;
  activeJourneys: number;
  totalEmailsSent: number;
  emailsThisMonth: number;
  totalOpens: number;
  totalClicks: number;
  averageOpenRate: number;
  averageClickRate: number;
  topCampaign?: {
    id: number;
    name: string;
    openRate: number;
    clickRate: number;
  };
  recentActivity: Array<{
    type: string;
    description: string;
    timestamp: string;
  }>;
}

interface CampaignAnalytics {
  campaignId: number;
  campaignName: string;
  totalSent: number;
  totalOpens: number;
  totalClicks: number;
  totalBounces: number;
  totalUnsubscribes: number;
  openRate: number;
  clickRate: number;
  bounceRate: number;
  unsubscribeRate: number;
  conversionRate: number;
  revenue?: number;
  totalCost?: number;
  costPerEmail?: number;
  costByStatus?: {
    sent: number;
    failed: number;
    processing: number;
  };
  emailsByDay: Array<{
    date: string;
    sent: number;
    opens: number;
    clicks: number;
  }>;
}

interface EmailCostData {
  totalEmails: number;
  totalCost: number;
  costPerEmail: number;
  costByPeriod: Array<{
    date: string;
    emails: number;
    cost: number;
  }>;
  costByStatus: {
    sent: { count: number; cost: number };
    failed: { count: number; cost: number };
    processing: { count: number; cost: number };
  };
  topCostCampaigns: Array<{
    campaignId: number;
    campaignName: string;
    emailCount: number;
    totalCost: number;
  }>;
  topCostJourneys: Array<{
    journeyId: number;
    journeyName: string;
    emailCount: number;
    totalCost: number;
  }>;
}

interface CostComparison {
  id: number;
  name: string;
  emailCount: number;
  totalCost: number;
  avgCostPerEmail: number;
  efficiency: number;
}

export default function CRMAnalyticsTab() {
  const [selectedTimeRange, setSelectedTimeRange] = useState("30");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  const safeFormatTimestamp = (timestamp?: string) => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "Unknown";
    return format(date, "MMM dd, HH:mm");
  };

  // Fetch overview analytics
  const { data: overviewData, isLoading: overviewLoading } = useQuery<AnalyticsOverview>({
    queryKey: ['/api/admin/crm/analytics/overview'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/crm/analytics/overview');
      return response.json();
    }
  });

  // Fetch time series data
  const { data: timeSeriesData, isLoading: timeSeriesLoading } = useQuery({
    queryKey: ['/api/admin/crm/analytics/timeseries', selectedTimeRange, selectedCampaignId],
    queryFn: async () => {
      const params = new URLSearchParams({
        days: selectedTimeRange
      });
      if (selectedCampaignId) params.append('campaignId', selectedCampaignId);
      
      const response = await apiRequest('GET', `/api/admin/crm/analytics/timeseries?${params}`);
      return response.json();
    }
  });

  // Fetch campaign analytics
  const { data: campaignAnalytics, isLoading: campaignAnalyticsLoading } = useQuery<CampaignAnalytics>({
    queryKey: ['/api/admin/crm/analytics/campaigns', selectedCampaignId],
    queryFn: async () => {
      if (!selectedCampaignId) return null;
      const response = await apiRequest('GET', `/api/admin/crm/analytics/campaigns/${selectedCampaignId}`);
      return response.json();
    },
    enabled: !!selectedCampaignId
  });

  // Fetch campaigns for dropdown
  const { data: campaignsData } = useQuery({
    queryKey: ['/api/admin/crm/campaigns'],
    queryFn: async () => {
      const response = await apiRequest('GET', '/api/admin/crm/campaigns?limit=100');
      return response.json();
    }
  });

  // Fetch traffic sources analytics
  const { data: trafficSources, isLoading: trafficSourcesLoading } = useQuery({
    queryKey: ['/api/admin/crm/analytics/traffic-sources', selectedTimeRange],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/crm/analytics/traffic-sources?days=${selectedTimeRange}`);
      return response.json();
    }
  });

  // Fetch email cost analytics
  const { data: emailCostData, isLoading: costLoading } = useQuery<EmailCostData>({
    queryKey: ['/api/admin/crm/analytics/costs', selectedTimeRange],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/crm/analytics/costs?days=${selectedTimeRange}`);
      const result = await response.json();
      return result.data;
    }
  });

  // Fetch campaign cost comparison
  const { data: campaignCostComparison } = useQuery<CostComparison[]>({
    queryKey: ['/api/admin/crm/analytics/costs/campaigns', selectedTimeRange],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/crm/analytics/costs/campaigns?days=${selectedTimeRange}&limit=10`);
      const result = await response.json();
      return result.data;
    }
  });

  // Fetch journey cost comparison
  const { data: journeyCostComparison } = useQuery<CostComparison[]>({
    queryKey: ['/api/admin/crm/analytics/costs/journeys', selectedTimeRange],
    queryFn: async () => {
      const response = await apiRequest('GET', `/api/admin/crm/analytics/costs/journeys?days=${selectedTimeRange}&limit=10`);
      const result = await response.json();
      return result.data;
    }
  });

  const campaigns = campaignsData?.campaigns || [];
  const timeSeries = timeSeriesData || [];
  const sources = trafficSources || [];

  const timeRangeOptions = [
    { value: "7", label: "Last 7 days" },
    { value: "30", label: "Last 30 days" },
    { value: "90", label: "Last 3 months" },
    { value: "365", label: "Last year" }
  ];

  const formatPercent = (value?: number) =>
    typeof value === "number" ? `${(value * 100).toFixed(1)}%` : "0.0%";

  const formatNumber = (value?: number | null) => {
    const safeValue = typeof value === "number" && !Number.isNaN(value) ? value : 0;
    return safeValue.toLocaleString();
  };

  const getChangeColor = (current: number, previous: number) => {
    if (current > previous) return "text-green-600";
    if (current < previous) return "text-red-600";
    return "text-gray-600";
  };

  const getChangeIcon = (current: number, previous: number) => {
    if (current > previous) return <ArrowUpRight className="h-4 w-4" />;
    if (current < previous) return <ArrowDownRight className="h-4 w-4" />;
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            CRM Analytics
          </h2>
          <p className="text-gray-600 mt-1">
            Track email campaign performance and user engagement
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          <div>
            <Label htmlFor="timeRange">Time Range</Label>
            <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
              <SelectTrigger className="w-[150px]" data-testid="select-time-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeRangeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="journeys">Journeys</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {overviewLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
              <p className="mt-2 text-gray-500">Loading analytics...</p>
            </div>
          ) : (
            <>
              {/* Email Cost Metrics */}
              <div className="mt-6">
                <h3 className="text-lg font-semibold mb-4">Email Cost Analytics</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Total Email Cost</p>
                          <p className="text-2xl font-bold text-green-600">
                            ${(emailCostData?.totalCost || 0).toFixed(2)}
                          </p>
                        </div>
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Mail className="h-6 w-6 text-green-600" />
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        {formatNumber(emailCostData?.totalEmails || 0)} emails sent
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Cost This Week</p>
                          <p className="text-2xl font-bold text-blue-600">
                            ${((emailCostData?.costByPeriod || [])
                              .slice(-7)
                              .reduce((sum, d) => sum + d.cost, 0))
                              .toFixed(2)}
                          </p>
                        </div>
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <TrendingUp className="h-6 w-6 text-blue-600" />
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        $0.0001 per email
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Cost This Month</p>
                          <p className="text-2xl font-bold text-purple-600">
                            ${((emailCostData?.costByPeriod || [])
                              .slice(-30)
                              .reduce((sum, d) => sum + d.cost, 0))
                              .toFixed(2)}
                          </p>
                        </div>
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Calendar className="h-6 w-6 text-purple-600" />
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        Last 30 days
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-gray-600">Successful Sends</p>
                          <p className="text-2xl font-bold text-orange-600">
                            ${(emailCostData?.costByStatus?.sent?.cost || 0).toFixed(2)}
                          </p>
                        </div>
                        <div className="p-2 bg-orange-100 rounded-lg">
                          <CheckCircle className="h-6 w-6 text-orange-600" />
                        </div>
                      </div>
                      <div className="mt-2 text-xs text-gray-500">
                        {formatNumber(emailCostData?.costByStatus?.sent?.count || 0)} sent
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Top Cost Campaigns Table */}
              {emailCostData?.topCostCampaigns && emailCostData.topCostCampaigns.length > 0 && (
                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Top Campaigns by Cost</CardTitle>
                    <CardDescription>
                      Campaigns with highest email costs
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-2 px-2">Campaign</th>
                            <th className="text-right py-2 px-2">Emails</th>
                            <th className="text-right py-2 px-2">Total Cost</th>
                            <th className="text-right py-2 px-2">Avg Cost</th>
                          </tr>
                        </thead>
                        <tbody>
                          {emailCostData.topCostCampaigns.map((campaign) => (
                            <tr key={campaign.campaignId} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-2">{campaign.campaignName}</td>
                              <td className="text-right py-2 px-2">{formatNumber(campaign.emailCount)}</td>
                              <td className="text-right py-2 px-2 font-semibold">
                                ${campaign.totalCost.toFixed(2)}
                              </td>
                              <td className="text-right py-2 px-2 text-gray-600">
                                $0.0001
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Performance Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>Email Performance Trends</CardTitle>
                  <CardDescription>
                    Email sends, opens, and clicks over time
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {timeSeriesLoading ? (
                    <div className="h-[300px] flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : timeSeries.length > 0 ? (
                    <div className="h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={timeSeries}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                          />
                          <YAxis />
                          <RechartsTooltip 
                            labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                          />
                          <Legend />
                          <Area
                            type="monotone"
                            dataKey="sent"
                            stackId="1"
                            stroke="#8884d8"
                            fill="#8884d8"
                            fillOpacity={0.3}
                            name="Emails Sent"
                          />
                          <Area
                            type="monotone"
                            dataKey="opens"
                            stackId="2"
                            stroke="#82ca9d"
                            fill="#82ca9d"
                            fillOpacity={0.3}
                            name="Opens"
                          />
                          <Area
                            type="monotone"
                            dataKey="clicks"
                            stackId="3"
                            stroke="#ffc658"
                            fill="#ffc658"
                            fillOpacity={0.3}
                            name="Clicks"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      No data available for the selected time range
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Traffic Sources Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle>Traffic Sources</CardTitle>
                  <CardDescription>
                    User registration sources comparison
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {trafficSourcesLoading ? (
                    <div className="h-[300px] flex items-center justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                  ) : sources.length > 0 ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Bar Chart */}
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={sources}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="label" />
                            <YAxis />
                            <RechartsTooltip />
                            <Legend />
                            <Bar 
                              dataKey="count" 
                              fill="#8884d8" 
                              name="Users"
                              radius={[8, 8, 0, 0]}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                      
                      {/* Pie Chart */}
                      <div className="h-[300px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={sources}
                              dataKey="count"
                              nameKey="label"
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              label={({ label, percent }) => `${label}: ${(percent * 100).toFixed(1)}%`}
                            >
                              {sources.map((entry: any, index: number) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={
                                    entry.source === 'web' ? '#3b82f6' :
                                    entry.source === 'facebook_lead_ad' ? '#1877F2' :
                                    entry.source === 'referral' ? '#10b981' :
                                    '#94a3b8'
                                  } 
                                />
                              ))}
                            </Pie>
                            <RechartsTooltip />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-gray-500">
                      No traffic source data available
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Top Performer & Recent Activity */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Top Performing Campaign</CardTitle>
                    <CardDescription>
                      Best campaign by open rate
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {overviewData?.topCampaign ? (
                      <div className="space-y-3">
                        <div>
                          <h4 className="font-medium">{overviewData.topCampaign.name}</h4>
                          <p className="text-sm text-gray-600">Campaign ID: {overviewData.topCampaign.id}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Open Rate</p>
                            <p className="text-xl font-semibold text-green-600">
                              {formatPercent(overviewData.topCampaign.openRate)}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Click Rate</p>
                            <p className="text-xl font-semibold text-blue-600">
                              {formatPercent(overviewData.topCampaign.clickRate)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-500">No campaign data available</p>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Recent Activity</CardTitle>
                    <CardDescription>
                      Latest CRM system events
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {overviewData?.recentActivity?.slice(0, 5).map((activity, index) => (
                        <div key={index} className="flex items-start gap-3">
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                          <div className="flex-1">
                            <p className="text-sm">{activity.description}</p>
                            <p className="text-xs text-gray-500">
                              {safeFormatTimestamp(activity.timestamp)}
                            </p>
                          </div>
                        </div>
                      )) || (
                        <p className="text-gray-500 text-sm">No recent activity</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Campaign Performance</h3>
            <div>
              <Label htmlFor="campaignSelect">Select Campaign</Label>
              <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
                <SelectTrigger className="w-[300px]" data-testid="select-campaign-analytics">
                  <SelectValue placeholder="Choose a campaign to analyze" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((campaign: any) => (
                    <SelectItem key={campaign.id} value={campaign.id.toString()}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {selectedCampaignId && campaignAnalytics ? (
            <div className="space-y-6">
              {/* Campaign Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Mail className="h-5 w-5 text-blue-600" />
                      <div>
                        <div className="text-2xl font-bold">{formatNumber(campaignAnalytics.totalSent)}</div>
                        <div className="text-sm text-gray-500">Emails Sent</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Eye className="h-5 w-5 text-green-600" />
                      <div>
                        <div className="text-2xl font-bold">{formatPercent(campaignAnalytics.openRate)}</div>
                        <div className="text-sm text-gray-500">Open Rate</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <MousePointer className="h-5 w-5 text-purple-600" />
                      <div>
                        <div className="text-2xl font-bold">{formatPercent(campaignAnalytics.clickRate)}</div>
                        <div className="text-sm text-gray-500">Click Rate</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-orange-600" />
                      <div>
                        <div className="text-2xl font-bold">{formatPercent(campaignAnalytics.conversionRate)}</div>
                        <div className="text-sm text-gray-500">Conversion Rate</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Campaign Performance Chart */}
              <Card>
                <CardHeader>
                  <CardTitle>{campaignAnalytics.campaignName} - Daily Performance</CardTitle>
                  <CardDescription>
                    Daily email sends, opens, and clicks for this campaign
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={campaignAnalytics.emailsByDay}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(value) => format(new Date(value), 'MMM dd')}
                        />
                        <YAxis />
                        <RechartsTooltip 
                          labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
                        />
                        <Legend />
                        <Line
                          type="monotone"
                          dataKey="sent"
                          stroke="#8884d8"
                          strokeWidth={2}
                          name="Sent"
                        />
                        <Line
                          type="monotone"
                          dataKey="opens"
                          stroke="#82ca9d"
                          strokeWidth={2}
                          name="Opens"
                        />
                        <Line
                          type="monotone"
                          dataKey="clicks"
                          stroke="#ffc658"
                          strokeWidth={2}
                          name="Clicks"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="text-center py-12">
              <BarChart3 className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Campaign</h3>
              <p className="text-gray-500">Choose a campaign from the dropdown to view detailed analytics.</p>
            </div>
          )}
        </TabsContent>

        {/* Journeys Tab - Cost Breakdown */}
        <TabsContent value="journeys" className="space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium">Journey Email Cost Breakdown</h3>
            <p className="text-sm text-gray-500">
              Top journeys by email volume and spend for the selected time range
            </p>
          </div>

          {journeyCostComparison && journeyCostComparison.length > 0 ? (
            <Card>
              <CardHeader>
                <CardTitle>Top Journeys by Cost</CardTitle>
                <CardDescription>
                  Sorted by total email cost (includes all email sends linked to a journey)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-2">Journey</th>
                        <th className="text-right py-2 px-2">Emails</th>
                        <th className="text-right py-2 px-2">Total Cost</th>
                        <th className="text-right py-2 px-2">Avg Cost</th>
                        <th className="text-right py-2 px-2">Efficiency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {journeyCostComparison.map((journey) => (
                        <tr
                          key={journey.id}
                          className="border-b hover:bg-gray-50"
                        >
                          <td className="py-2 px-2">
                            {journey.name || `Journey ${journey.id}`}
                          </td>
                          <td className="text-right py-2 px-2">
                            {formatNumber(journey.emailCount)}
                          </td>
                          <td className="text-right py-2 px-2 font-semibold">
                            ${journey.totalCost.toFixed(2)}
                          </td>
                          <td className="text-right py-2 px-2 text-gray-600">
                            ${journey.avgCostPerEmail.toFixed(4)}
                          </td>
                          <td className="text-right py-2 px-2 text-gray-600">
                            {journey.efficiency.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No Journey Cost Data
              </h3>
              <p className="text-gray-500">
                Journey cost analytics will appear here once journeys send
                emails within the selected time range.
              </p>
            </div>
          )}
        </TabsContent>

        {/* Performance Tab */}
        <TabsContent value="performance" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Email Delivery Status</CardTitle>
                <CardDescription>Distribution of email delivery outcomes</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[250px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        dataKey="value"
                        data={[
                          { name: 'Delivered', value: overviewData?.totalEmailsSent || 0, fill: '#10b981' },
                          { name: 'Bounced', value: Math.floor((overviewData?.totalEmailsSent || 0) * 0.02), fill: '#ef4444' },
                          { name: 'Pending', value: Math.floor((overviewData?.totalEmailsSent || 0) * 0.01), fill: '#f59e0b' }
                        ]}
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      />
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Engagement Metrics</CardTitle>
                <CardDescription>User interaction with email campaigns</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Eye className="h-5 w-5 text-blue-600" />
                      <span className="font-medium">Total Opens</span>
                    </div>
                    <span className="text-xl font-bold">{formatNumber(overviewData?.totalOpens || 0)}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <MousePointer className="h-5 w-5 text-green-600" />
                      <span className="font-medium">Total Clicks</span>
                    </div>
                    <span className="text-xl font-bold">{formatNumber(overviewData?.totalClicks || 0)}</span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Target className="h-5 w-5 text-purple-600" />
                      <span className="font-medium">Click-to-Open Rate</span>
                    </div>
                    <span className="text-xl font-bold">
                      {overviewData?.totalOpens ? 
                        formatPercent((overviewData.totalClicks || 0) / overviewData.totalOpens) : 
                        '0.0%'
                      }
                    </span>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Users className="h-5 w-5 text-orange-600" />
                      <span className="font-medium">Unique Engagement</span>
                    </div>
                    <span className="text-xl font-bold">
                      {Math.floor((overviewData?.totalOpens || 0) * 0.7).toLocaleString()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
